
import React from 'react';
import { Users, UserCheck, TrendingUp, Shirt, Eye, EyeOff, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { motion } from 'framer-motion';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { TShirtStats } from '@/hooks/admin/useTShirtStats';
import { useIsMobile } from '@/hooks/use-mobile';
import { Progress } from '@/components/ui/progress';
import { useContributionVisibility } from '@/hooks/admin/useContributionVisibility';

interface AdminStatsProps {
  totalRegistered: number;
  totalAttended: number;
  totalContribution: number;
  attendancePercentage: number;
  tShirtStats: TShirtStats;
  loading: boolean;
}

const AdminStats: React.FC<AdminStatsProps> = ({ 
  totalRegistered, 
  totalAttended, 
  totalContribution, 
  attendancePercentage,
  tShirtStats,
  loading 
}) => {
  const isMobile = useIsMobile();
  const { isVisible: showContributionData, toggleVisibility: toggleContributionVisibility } = useContributionVisibility();
  
  const getProgressColor = () => {
    if (attendancePercentage < 30) return "bg-rose-500";
    if (attendancePercentage < 60) return "bg-amber-500";
    return "bg-emerald-500";
  };

  const getTrendIndicator = () => {
    if (attendancePercentage > 80) {
      return <ArrowUpRight className="h-4 w-4 text-emerald-500" />;
    } else if (attendancePercentage < 30) {
      return <ArrowDownRight className="h-4 w-4 text-rose-500" />;
    }
    return null;
  };
  
  const renderContributionCard = () => {
    return (
      <Card className="shadow-md overflow-hidden bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border-amber-100 hover:shadow-lg transition-shadow duration-300">
        <CardHeader className="pb-2 space-y-0">
          <div className="flex justify-between items-center">
            <CardTitle className="text-sm font-medium text-amber-700 dark:text-amber-400 flex items-center">
              <TrendingUp className="h-4 w-4 mr-2" />
              Đóng góp
            </CardTitle>
            {isMobile && (
              <motion.button 
                whileTap={{ scale: 0.95 }}
                onClick={toggleContributionVisibility}
                className="p-3 rounded-full bg-amber-100 text-amber-700 hover:bg-amber-200 transition-colors z-10 touch-target"
                aria-label={showContributionData ? "Ẩn thông tin đóng góp" : "Hiển thị thông tin đóng góp"}
              >
                {showContributionData ? (
                  <EyeOff className="h-5 w-5" />
                ) : (
                  <Eye className="h-5 w-5 animate-pulse-subtle" />
                )}
              </motion.button>
            )}
          </div>
        </CardHeader>
        <CardContent className="pt-2">
          {isMobile && !showContributionData ? (
            <div className="py-5 text-center bg-amber-50/50 rounded-md border border-amber-100/50 shadow-sm touch-target" onClick={toggleContributionVisibility}>
              <span className="text-sm text-amber-600 font-medium flex items-center justify-center gap-1.5">
                <Eye className="h-5 w-5 animate-gentle-bounce" />
                Nhấn để xem thông tin đóng góp
              </span>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground text-xs">Tổng đóng góp</span>
                <motion.span 
                  className="text-lg font-bold text-amber-600 bg-amber-50 px-3 py-1 rounded-full shadow-sm border border-amber-100"
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  {loading ? '...' : totalContribution.toLocaleString('vi-VN')}
                  <span className="text-xs ml-1 font-normal">VNĐ</span>
                </motion.span>
              </div>
              <div className="flex items-center justify-between mt-3">
                <span className="text-muted-foreground text-xs">Trung bình</span>
                <motion.span 
                  className="text-sm font-medium text-amber-600 bg-amber-50/80 px-2 py-0.5 rounded-full shadow-sm border border-amber-100/70"
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: 0.1 }}
                >
                  {loading || totalRegistered === 0 ? '...' : 
                    Math.round(totalContribution / totalRegistered).toLocaleString('vi-VN')}
                  <span className="text-xs ml-1 font-normal">VNĐ/người</span>
                </motion.span>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    );
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6"
    >
      <Card className="shadow-md overflow-hidden bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-blue-100 hover:shadow-lg transition-shadow duration-300">
        <CardHeader className="pb-2 space-y-0 relative">
          <div className="flex justify-between items-center">
            <CardTitle className="text-sm font-medium text-blue-700 dark:text-blue-400 flex items-center">
              <Users className="h-4 w-4 mr-2" />
              Tổng thể
            </CardTitle>
            <Badge 
              variant="info"
              className="text-xs flex items-center gap-1"
            >
              {loading ? '...' : `${totalAttended}/${totalRegistered}`}
              {getTrendIndicator()}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="pt-2 pb-3">
          <div className="grid grid-cols-2 gap-1 text-xs">
            <div className="flex flex-col">
              <span className="text-muted-foreground">Đăng ký</span>
              <motion.span 
                className="text-lg font-bold text-blue-600"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3 }}
              >
                {loading ? '...' : totalRegistered}
              </motion.span>
            </div>
            <div className="flex flex-col">
              <span className="text-muted-foreground">Đã tham dự</span>
              <motion.span 
                className="text-lg font-bold text-emerald-600"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: 0.1 }}
              >
                {loading ? '...' : totalAttended}
              </motion.span>
            </div>
          </div>
          
          <div className="mt-3">
            <div className="flex justify-between items-center mb-1">
              <span className="text-xs text-muted-foreground">Tỉ lệ tham dự</span>
              <span className="text-xs font-medium text-blue-700">
                {loading ? '...' : `${Math.round(attendancePercentage)}%`}
              </span>
            </div>
            <Progress 
              value={Math.max(3, attendancePercentage)} 
              className="h-3" 
            />
          </div>
        </CardContent>
      </Card>
      
      {renderContributionCard()}
      
      <Card className="shadow-md overflow-hidden bg-gradient-to-br from-purple-50 to-violet-50 dark:from-purple-900/20 dark:to-violet-900/20 border-purple-100 hover:shadow-lg transition-shadow duration-300">
        <CardHeader className="pb-2 space-y-0">
          <div className="flex justify-between items-center">
            <CardTitle className="text-sm font-medium text-purple-700 dark:text-purple-400 flex items-center">
              <Shirt className="h-4 w-4 mr-2" />
              Áo Kỷ Niệm
            </CardTitle>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge 
                    variant="outline" 
                    className="text-xs bg-purple-100 text-purple-700 border-purple-200 cursor-help"
                  >
                    {loading ? '...' : `${tShirtStats.totalDistributed}/${tShirtStats.totalSized}`}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="text-xs">Đã phát / Tổng số</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </CardHeader>
        <CardContent className="pt-2">
          <div className="flex flex-wrap gap-2 justify-between">
            <motion.div 
              className="flex items-center gap-1.5"
              initial={{ opacity: 0, x: -5 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div className="h-3 w-3 rounded-full bg-blue-500"></div>
              <span className="text-xs">Nam: {tShirtStats.totalMen}</span>
            </motion.div>
            <motion.div 
              className="flex items-center gap-1.5"
              initial={{ opacity: 0, x: -5 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, delay: 0.1 }}
            >
              <div className="h-3 w-3 rounded-full bg-pink-500"></div>
              <span className="text-xs">Nữ: {tShirtStats.totalWomen}</span>
            </motion.div>
            <motion.div 
              className="flex items-center gap-1.5"
              initial={{ opacity: 0, x: -5 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, delay: 0.2 }}
            >
              <div className="h-3 w-3 rounded-full bg-green-500"></div>
              <span className="text-xs">Đã phát: {tShirtStats.totalDistributed}</span>
            </motion.div>
            <motion.div 
              className="flex items-center gap-1.5"
              initial={{ opacity: 0, x: -5 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, delay: 0.3 }}
            >
              <div className="h-3 w-3 rounded-full bg-blue-300"></div>
              <span className="text-xs">Còn lại: {tShirtStats.totalRemaining}</span>
            </motion.div>
          </div>
          
          <div className="mt-3">
            <div className="flex justify-between items-center mb-1">
              <span className="text-xs text-muted-foreground">Tỉ lệ phát:</span>
              <span className="text-xs font-medium text-purple-700">
                {Math.round((tShirtStats.totalDistributed / (tShirtStats.totalSized || 1)) * 100) || 0}%
              </span>
            </div>
            <Progress 
              value={Math.max(3, (tShirtStats.totalDistributed / (tShirtStats.totalSized || 1)) * 100) || 0} 
              className="h-3"
            />
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default AdminStats;
